import { useCallback } from "react";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";

import type { IFieldProps } from "../field";

import { Textarea } from "@components/formControls";
import type { IIconProps } from "@components/atoms/icons";
import type { IIconName } from "@components/icons";
import { Field } from "@/components/fastFields/field/field";
import type { IExtends } from "@/utils";
import { useReadOnly } from "@/hooks";

type ITypes = IExtends<
  ParseKeys<"form">,
  | "noteForExpert"
  | "briefDescription"
  | "description"
  | "pattern_description"
  | "medical_orders"
  | "education"
  | "visit-reason"
  | "comment"
  | "addendum"
  | "centerDescription"
>;

const iconMap: { [key in ITypes]: IIconName | IIconProps } = {
  noteForExpert: "Notes",
  briefDescription: "Notebook",
  description: "Notes",
  pattern_description: "Notes",
  medical_orders: "Pills",
  education: "SquareAcademicCap",
  "visit-reason": "Notes",
  comment: "Notes",
  addendum: "AddCircle",
  centerDescription: "InfoSquare",
};

type IProps<T> = {
  customTitle?: string;
  type: ITypes;
  disabled?: boolean;
  placeholder?: string;
  readonly?: boolean;
  onChange?: (val: string) => void;
} & Required<Pick<IFieldProps<T>, "formik" | "name">> &
  Partial<Pick<IFieldProps<T>, "icon" | "title" | "isRequired">>;

export function FastTextarea<T>(props: IProps<T>) {
  const { t } = useTranslation("form");
  const readonly = useReadOnly(props.readonly);
  const title = props.title || props.type;
  const placeholder = props.placeholder || t(title);
  const name = props.name;
  const { values, setFieldValue, errors, touched, handleBlur } = props.formik;
  const value = values[name] as string;

  const icon = props.icon || iconMap[props.type];

  const changeHandler = useCallback(
    (val: string) => {
      props?.onChange?.(val);
      setFieldValue(name.toString(), val);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange],
  );

  return (
    <Field
      name={name}
      title={title}
      formik={props.formik}
      icon={icon}
      customTitle={props.customTitle}
      isRequired={props.isRequired && !readonly}
    >
      <Textarea
        placeholder={placeholder}
        id={name.toString()}
        onChangeValue={changeHandler}
        onBlur={handleBlur}
        value={value}
        validation={touched[name] && errors[name] ? "error" : undefined}
        readOnly={readonly}
        disabled={props.disabled}
      />
    </Field>
  );
}
